import { Dropdown } from 'antd';
import _ from 'lodash';
import { TOOLBAR_TYPE } from '../../../config';
import type { ECGToolButtonProps } from './ecgToolButton.types';
import ToolbarArrowIcon from './assets/ecg-toolbar-arrow-icon.svg';

const spdMenu = [2.5, 10, 25, 50, 75, 100, 200, 500];
const ampMenu = [1, 2, 5, 10, 20, 50, 100, 200];
const hiPMenu = [0, 0.05, 0.5, 1, 2];
const loPMenu = [0, 15, 30, 40, 50, 60, 100];

/**
 * Toolbar control exposing one ECG display setting - sweep speed, gain, high-pass or low-pass filter - as a dropdown. Delegates rendering to renderDropdown, which builds the unit-formatted option list for the tool type and preselects the entry matching value via getDefaultKeys, while onClickItem reports the picked value through onChange unless disabled.
 *
 * @param value - Currently applied value of the setting.
 * @param type - Toolbar type selecting which option list and unit are used.
 * @param onChange - Callback receiving the newly selected value.
 * @param disabled - Whether the dropdown is disabled.
 * @returns The rendered toolbar control element.
 */
const ECGToolButton = ({ value, type, onChange, disabled }: ECGToolButtonProps) => {
  /**
   * Propagates a toolbar dropdown selection to the parent. Calls onChange with the value prop carried by the clicked menu entry.
   *
   * @param item - The clicked menu entry holding the selected value.
   * @returns Nothing.
   */
  const onClickItem = ({ item }: any) => {
    onChange(item.props.value);
  };

  /**
   * Computes the default selected dropdown key for the toolbar's current value. Locates the item index whose value matches with lodash findIndex and returns a single key prefixed by the tool type - speed, gain, hiP or loP - or an empty array for any other type.
   *
   * @param items - Dropdown menu items to search for the current value.
   * @returns The default selected key, or an empty array.
   */
  const getDefaultKeys = (items: any) => {
    /**
     * Finds the index of the dropdown item whose value matches the current toolbar value. Uses lodash findIndex over items.
     *
     * @returns The matching item index, or -1.
     */
    const defaultIndex = _.findIndex(items, (x: any) => x.value === value);
    switch (type) {
      case TOOLBAR_TYPE.SPEED: {
        return [`speed-${defaultIndex}`];
      }
      case TOOLBAR_TYPE.GAIN: {
        return [`gain-${defaultIndex}`];
      }
      case TOOLBAR_TYPE.HIGH_PASS: {
        return [`hiP-${defaultIndex}`];
      }
      case TOOLBAR_TYPE.LOW_PASS: {
        return [`loP-${defaultIndex}`];
      }
      default: {
        return [];
      }
    }
  };

  /**
   * Builds the toolbar dropdown menu and its trigger label for the active tool type. Fills the item list from spdMenu, ampMenu, hiPMenu or loPMenu, labels values in mm/s, mm/mV or Hz - rendering No for 0 and marking 50 and 60 Hz as Notch - highlights the entry matching the current value, and returns an Ant Design Dropdown wired to getDefaultKeys and onClickItem.
   *
   * @returns The rendered dropdown element.
   */
  const renderDropdown = (): any => {
    const items: any = [];
    let formatText;
    switch (type) {
      case TOOLBAR_TYPE.SPEED: {
        _.forEach(spdMenu, (x, i) =>
          items.push({
            key: `speed-${i}`,
            label: <div className={x === value ? 'selected' : ''}>{`${x} mm/s`}</div>,
            value: x,
          }),
        );
        formatText = (
          <button type="button" className="toolbar-text">
            <div className="type">{TOOLBAR_TYPE.SPEED}:</div>
            <div className="value">{(value * 10) % 10 === 0 ? value : value.toFixed(1)}</div>
            <img src={ToolbarArrowIcon} alt="arrow-icon" />
          </button>
        );
        break;
      }
      case TOOLBAR_TYPE.GAIN: {
        _.forEach(ampMenu, (x, i) =>
          items.push({
            key: `gain-${i}`,
            label: <div className={x === value ? 'selected' : ''}>{`${x} mm/mV`}</div>,
            value: x,
          }),
        );
        formatText = (
          <button type="button" className="toolbar-text">
            <div className="type">{TOOLBAR_TYPE.GAIN}:</div>
            <div className="value">{value}</div>
            <img src={ToolbarArrowIcon} alt="arrow-icon" />
          </button>
        );
        break;
      }
      case TOOLBAR_TYPE.HIGH_PASS: {
        _.forEach(hiPMenu, (x, i) =>
          items.push({
            key: `hiP-${i}`,
            label: (
              <div className={x === value ? 'selected' : ''}>{x !== 0 ? `${x} Hz` : 'No'}</div>
            ),
            value: x,
          }),
        );
        formatText = (
          <button type="button" className="toolbar-text">
            <div className="type">{TOOLBAR_TYPE.HIGH_PASS}:</div>
            <div className="value">{value !== 0 ? value : 'No'}</div>
            <img src={ToolbarArrowIcon} alt="arrow-icon" />
          </button>
        );
        break;
      }
      case TOOLBAR_TYPE.LOW_PASS: {
        _.forEach(loPMenu, (x, i) =>
          items.push({
            key: `loP-${i}`,
            label: (
              <div className={x === value ? 'selected' : ''}>
                {x !== 0 ? (x !== 50 && x !== 60 ? `${x} Hz` : `${x} Hz (Notch)`) : 'No'}
              </div>
            ),
            value: x,
          }),
        );
        formatText = (
          <button type="button" className="toolbar-text">
            <div className="type">{TOOLBAR_TYPE.LOW_PASS}:</div>
            <div className="value">{value !== 0 ? value : 'No'}</div>
            <img src={ToolbarArrowIcon} alt="arrow-icon" />
          </button>
        );
        break;
      }
      default: {
        break;
      }
    }
    return (
      <Dropdown
        disabled={disabled}
        trigger={['click']}
        menu={{
          items,
          selectable: true,
          defaultSelectedKeys: getDefaultKeys(items),
          onClick: onClickItem,
        }}
      >
        {formatText}
      </Dropdown>
    );
  };
  return renderDropdown();
};

export default ECGToolButton;
